import React, { useEffect, useState } from 'react';
import { ITEMS_THEME } from './itemsTheme';
import ItemFormPanel from './ItemFormPanel';
import ItemListHistory from './ItemListHistory';
import { Package, Plus } from 'lucide-react';

export default function ItemsCatalogTab({
  formSectionRef,
  editingItemId,
  filteredCount,
  totalCount,
  activeCount,
  message,
  formProps,
  historyProps,
}) {
  const theme = ITEMS_THEME;
  const isEdit = Boolean(editingItemId);
  const [isFormExpanded, setIsFormExpanded] = useState(false);

  useEffect(() => {
    if (editingItemId) setIsFormExpanded(true);
  }, [editingItemId]);

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full w-full overflow-visible lg:overflow-hidden">
      {message && (
        <div
          role="status"
          className={`shrink-0 mb-2 rounded-xl px-3 py-2 text-sm border ${
            message.includes('Error')
              ? 'bg-red-50 text-red-800 border-red-200'
              : 'bg-emerald-50 text-emerald-800 border-emerald-200'
          }`}
        >
          {message}
        </div>
      )}

      <div className={`${theme.contentCard} flex flex-col flex-1 min-h-0 h-full overflow-visible lg:overflow-hidden`}>
        <div className={`${theme.panelHeader} shrink-0 py-2.5 sm:py-3`}>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600 min-w-0">
            <Package className={`h-4 w-4 shrink-0 ${theme.iconAccent}`} aria-hidden />
            <span className="font-semibold text-slate-800">{theme.historyTitle}</span>
            <span className="text-slate-400 hidden sm:inline">·</span>
            <span className={theme.statPill}>
              <span className={theme.statPillStrong}>{totalCount}</span> total
            </span>
            <span className={theme.statPill}>
              <span className={theme.statPillStrong}>{activeCount}</span> active
            </span>
          </div>
        </div>

        <div className="lg:hidden px-3 pt-3 shrink-0">
          <button
            type="button"
            onClick={() => setIsFormExpanded(!isFormExpanded)}
            aria-expanded={isFormExpanded}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-colors ${
              isFormExpanded
                ? theme.subNavActive
                : `${theme.subNavIdle} border-amber-100 bg-white`
            }`}
          >
            <Plus className="h-4 w-4" />
            {isFormExpanded
              ? isEdit
                ? 'Hide edit form'
                : 'Hide add form'
              : isEdit
                ? 'Edit item'
                : 'Add new item'}
          </button>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-12 lg:divide-x lg:items-stretch flex-1 min-h-0 overflow-visible lg:overflow-hidden ${theme.divider}`}
        >
          <section
            ref={formSectionRef}
            className={`order-2 lg:order-1 lg:col-span-5 xl:col-span-5 min-w-0 lg:min-w-[22rem] xl:min-w-[26rem] ${theme.formPanelBg} ${
              isFormExpanded ? 'flex flex-col' : 'hidden'
            } lg:flex lg:flex-col lg:min-h-0 lg:max-h-full lg:overflow-hidden`}
            aria-label="Add or edit item"
          >
            <div className="shrink-0 px-3 sm:px-4 pt-3 pb-2 border-b border-amber-100/80 bg-amber-50/40 flex items-center justify-between gap-2">
              <div>
                <h2 className="text-sm font-bold text-slate-900">{isEdit ? 'Edit item' : 'New item'}</h2>
                <p className="text-xs text-slate-500 mt-0.5">Product / service details</p>
              </div>
              {isEdit && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded shrink-0">
                  Editing
                </span>
              )}
            </div>
            <div className="p-3 sm:p-4 flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
              <ItemFormPanel {...formProps} embedded editingItemId={editingItemId} />
            </div>
          </section>

          <section
            className="order-1 lg:order-2 lg:col-span-7 xl:col-span-7 min-w-0 flex flex-col flex-1 min-h-0 max-h-full overflow-visible lg:overflow-hidden"
            aria-label="All items"
          >
            <ItemListHistory
              {...historyProps}
              embedded
              splitLayout
              matchedCount={filteredCount}
              totalCount={totalCount}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
